import { useDispatch, useSelector } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import { Divider, H, Grid, Wrapper, P } from '@farewill/ui'
import { Form, Formik } from 'formik'
import merge from 'lodash/merge'
import styled from 'styled-components'

import ENV from 'config/environment'
import { updateFuneralPlan } from 'state/actions'
import { formatValuesForFormik, strToBool } from 'utils/helpers'
import { FuneralPlan, FuneralPlanAttributes } from 'lib/models/funeral-plan'

import CriticalNotes from 'components/critical-notes'
import ContactDetails from './contact-details'
import Vulnerabilities from './vulnerabilities'
import CoveredIndividual from './covered-individual'
import Crematorium from './crematorium'
import CelebrationInterest from './celebration-interest'
import { CelebrationInterestEditable } from './celebration-interest-editable'
import CelebrationDetails from './celebration-details'
import UrnType from './urn-type'
import { SaveFuneralPlanField } from './types'
import MessageBox from 'components/message-box'
import {
  getCelebrationInterestStatus,
  getPlanUrnType,
} from './celebration-helpers'

const PlanDetailsFieldset = styled.fieldset`
  border: 0;
  margin: 0;
  min-width: 0;
  padding: 0;

  &:disabled {
    pointer-events: none;
  }
`

/* eslint-disable @typescript-eslint/no-explicit-any */
function transformObject(obj: { [key: string]: any }): { [key: string]: any } {
  const result: { [key: string]: any } = {}

  for (const key of Object.keys(obj)) {
    let current: { [key: string]: any } = result
    const parts = key.split('.')

    for (const part of parts) {
      if (!(part in current)) {
        current[part] = {}
      }
      if (parts[parts.length - 1] === part) {
        current[part] = obj[key] !== '' ? obj[key] : null
      } else {
        current = current[part]
      }
    }
  }
  return result
}
/* eslint-enable @typescript-eslint/no-explicit-any */

const CaseDetails = (): React.ReactElement => {
  const { id: funeralPlanId } = useParams<{ id: string }>()
  const dispatch = useDispatch()
  const data = useSelector(
    (state: { funeralPlanCase: FuneralPlan }) => state.funeralPlanCase
  )
  const { purchaser, purchaserIsCoveredIndividual, coveredIndividualDetails } =
    data.attributes

  const getWhoThePlanIsForInfo = (): string => {
    const purchaserName = `${purchaser.firstName} ${purchaser.lastName}`
    const coveredIndividualName = `${coveredIndividualDetails.firstName} ${coveredIndividualDetails.lastName}`
    const isFemale = coveredIndividualDetails.sex === 'female'

    return purchaserIsCoveredIndividual
      ? `${purchaserName} bought the plan for ${
          isFemale ? 'herself' : 'himself'
        }`
      : `${purchaserName} bought the plan for ${coveredIndividualName}`
  }

  const onSubmit = async (values: FuneralPlanAttributes) =>
    dispatch(
      updateFuneralPlan(funeralPlanId, {
        criticalNotes: values.criticalNotes || null,
        isPostalService: strToBool(values.isPostalService),
      })
    )

  const saveFuneralPlanField: SaveFuneralPlanField = ({ name, value }) => {
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    let transformedAttributes: { [key: string]: any } = {}

    if (name.split('.').length === 1) {
      transformedAttributes = { [name]: value !== '' ? value : null }
    } else {
      transformedAttributes = transformObject({ [name]: value })
      Object.keys(transformedAttributes).forEach((attrKey) => {
        transformedAttributes[attrKey] = merge(
          data.attributes[attrKey as keyof FuneralPlan['attributes']],
          transformedAttributes[attrKey]
        )
      })
    }

    return dispatch(
      updateFuneralPlan(funeralPlanId, transformedAttributes)
    ) as unknown as Promise<boolean>
  }

  const showCelebrationFields =
    getCelebrationInterestStatus({
      items: data.attributes.items,
      celebrationPurchase: data.attributes.celebrationPurchase,
    }) !== 'legacy'
  const isRedeemed = data.attributes.status === 'closed'

  return (
    <Wrapper>
      <H size="M">Plan details</H>
      <Grid>
        <Grid.Item>
          <P size="L">{getWhoThePlanIsForInfo()}</P>
        </Grid.Item>
        {data.attributes.coupledPlanId && (
          <Grid.Item>
            <MessageBox>
              This plan was purchased as part of a couple.{' '}
              <Link to={`/funeral-plans/${data.attributes.coupledPlanId}`}>
                Go to the linked plan.
              </Link>
            </MessageBox>
          </Grid.Item>
        )}
        <Grid.Item>
          {isRedeemed && (
            <MessageBox stretch margin={[0, 0, 'M']}>
              This funeral plan has been redeemed, so it is no longer editable.
            </MessageBox>
          )}
          <Formik
            initialValues={{
              ...formatValuesForFormik(data.attributes),
              urnType: getPlanUrnType(data.attributes.items),
            }}
            onSubmit={onSubmit}
            enableReinitialize
          >
            <Form>
              <PlanDetailsFieldset
                disabled={isRedeemed}
                data-testid="funeral-plan-details-fields"
              >
                <CriticalNotes />
                <Divider margin={['L', 0]} />
                <ContactDetails disabled={isRedeemed} />
                <Divider margin={['L', 0]} />
                {showCelebrationFields && (
                  <>
                    <Crematorium
                      handleSave={saveFuneralPlanField}
                      customerPostcode={purchaser.addresses?.[0]?.postalCode}
                    />
                    <Divider margin={['L', 0]} />
                    {ENV.FF_CELEBRATION_PHASE_2_ENABLED ? (
                      <CelebrationInterestEditable
                        handleSave={saveFuneralPlanField}
                      />
                    ) : (
                      <CelebrationInterest handleSave={saveFuneralPlanField} />
                    )}
                    <CelebrationDetails
                      canRemove={ENV.FF_CELEBRATION_PHASE_2_ENABLED}
                    />
                    <UrnType handleSave={saveFuneralPlanField} />
                    <Divider margin={['L', 0]} />
                  </>
                )}
                <Vulnerabilities handleSave={saveFuneralPlanField} />
                <Divider margin={['L', 0]} />
                <CoveredIndividual
                  handleSave={saveFuneralPlanField}
                  purchaserIsCoveredIndividual={purchaserIsCoveredIndividual}
                />
              </PlanDetailsFieldset>
            </Form>
          </Formik>
        </Grid.Item>
      </Grid>
      <Divider margin={['L', 0]} />
    </Wrapper>
  )
}

export default CaseDetails
